import {
  InspectorPortalAssignmentAction,
  InspectorPortalAssignmentActionResponseSchema,
  InspectorPortalAssignmentDetailResponseSchema,
  InspectorPortalAssignmentUploadResponseSchema,
  InspectorPortalAssignmentsQuery,
  InspectorPortalAssignmentsResponse,
  InspectorPortalAssignmentsResponseSchema,
  InspectorPortalAuthLogoutResponseSchema,
  InspectorPortalAuthVerifyOtpResponseSchema,
  ResponseMessageSchema,
  StatusSchema,
} from "@/schemas";
import { apiCall } from "@/config/axiosConfig";
import { API_ROUTES } from "@/config/appConfig";
import { fetchWithZod } from "@/utils/zodGuard";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

export const INSPECTOR_PORTAL_QUERY_KEYS = {
  BASE: ["inspector-portal"] as const,
  ASSIGNMENTS: () =>
    [...INSPECTOR_PORTAL_QUERY_KEYS.BASE, "assignments"] as const,
  ASSIGNMENT_DETAIL: (assignmentId?: string) =>
    [...INSPECTOR_PORTAL_QUERY_KEYS.ASSIGNMENTS(), assignmentId] as const,
};

const InspectorPortalAuthRequestOtpResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({ requested: z.literal(true) }),
});
const InspectorPortalAuthSessionResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    inspectorId: z.string().min(1),
    fullName: z.string().min(1),
  }),
});
const InspectorPortalDeleteFileResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({ deleted: z.literal(true) }),
});

export function parseInspectorPortalAuthRequestOtpResponse(response: unknown) {
  return InspectorPortalAuthRequestOtpResponseSchema.parse(response).data;
}

export function parseInspectorPortalAuthSessionResponse(response: unknown) {
  return InspectorPortalAuthSessionResponseSchema.parse(response).data;
}

export const apiCalls = {
  requestInspectorPortalOtp: async (phone: string) => {
    const response = await apiCall({
      method: "post",
      endpoint: API_ROUTES.INSPECTOR_PORTAL.AUTH.REQUEST_OTP,
      data: { phone },
    });

    return parseInspectorPortalAuthRequestOtpResponse(response);
  },
  resendInspectorPortalOtp: async (phone: string) => {
    const response = await apiCall({
      method: "post",
      endpoint: API_ROUTES.INSPECTOR_PORTAL.AUTH.REQUEST_OTP,
      data: { phone },
    });

    return parseInspectorPortalAuthRequestOtpResponse(response);
  },
  verifyInspectorPortalOtp: async (phone: string, otp: string) => {
    const response = await apiCall({
      method: "post",
      endpoint: API_ROUTES.INSPECTOR_PORTAL.AUTH.VERIFY_OTP,
      data: { phone, otp },
    });

    return InspectorPortalAuthVerifyOtpResponseSchema.parse(response).data;
  },
  getInspectorPortalSession: async () => {
    const response = await apiCall({
      method: "get",
      endpoint: API_ROUTES.INSPECTOR_PORTAL.AUTH.SESSION,
    });

    return parseInspectorPortalAuthSessionResponse(response);
  },
  logoutInspectorPortal: async () => {
    const response = await apiCall({
      method: "post",
      endpoint: API_ROUTES.INSPECTOR_PORTAL.AUTH.LOGOUT,
    });

    return InspectorPortalAuthLogoutResponseSchema.parse(response).data;
  },
  getInspectorPortalAssignments: async (
    query?: Partial<InspectorPortalAssignmentsQuery>,
  ) =>
    fetchWithZod(InspectorPortalAssignmentsResponseSchema, {
      method: "get",
      endpoint: API_ROUTES.INSPECTOR_PORTAL.ASSIGNMENTS.GET_ALL,
      params: query,
    }),
  getInspectorPortalAssignmentDetail: async (assignmentId: string) =>
    fetchWithZod(InspectorPortalAssignmentDetailResponseSchema, {
      method: "get",
      endpoint:
        API_ROUTES.INSPECTOR_PORTAL.ASSIGNMENTS.GET_DETAIL(assignmentId),
    }),
  acceptInspectorPortalAssignment: async (
    assignmentId: string,
    action: InspectorPortalAssignmentAction,
  ) =>
    fetchWithZod(InspectorPortalAssignmentActionResponseSchema, {
      method: "post",
      endpoint: API_ROUTES.INSPECTOR_PORTAL.ASSIGNMENTS.ACCEPT(assignmentId),
      data: action,
    }),
  declineInspectorPortalAssignment: async (
    assignmentId: string,
    action: InspectorPortalAssignmentAction,
  ) =>
    fetchWithZod(InspectorPortalAssignmentActionResponseSchema, {
      method: "post",
      endpoint: API_ROUTES.INSPECTOR_PORTAL.ASSIGNMENTS.DECLINE(assignmentId),
      data: action,
    }),
  scheduleInspectorPortalAssignment: async (
    assignmentId: string,
    action: InspectorPortalAssignmentAction,
  ) =>
    fetchWithZod(InspectorPortalAssignmentActionResponseSchema, {
      method: "post",
      endpoint: API_ROUTES.INSPECTOR_PORTAL.ASSIGNMENTS.SCHEDULE(assignmentId),
      data: action,
    }),
  uploadInspectorPortalAssignmentFiles: async (
    assignmentId: string,
    files: File[],
  ) => {
    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    return fetchWithZod(InspectorPortalAssignmentUploadResponseSchema, {
      method: "post",
      endpoint: API_ROUTES.INSPECTOR_PORTAL.ASSIGNMENTS.FILES(assignmentId),
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
  deleteInspectorPortalAssignmentFile: async (
    assignmentId: string,
    fileId: number,
  ) => {
    const response = await apiCall({
      method: "delete",
      endpoint: API_ROUTES.INSPECTOR_PORTAL.ASSIGNMENTS.FILE(
        assignmentId,
        fileId,
      ),
    });

    return InspectorPortalDeleteFileResponseSchema.parse(response).data;
  },
  downloadInspectorPortalAssignmentFile: async (
    assignmentId: string,
    fileId: number,
  ) =>
    apiCall<Blob>({
      method: "get",
      endpoint: API_ROUTES.INSPECTOR_PORTAL.ASSIGNMENTS.DOWNLOAD_FILE(
        assignmentId,
        fileId,
      ),
      responseType: "blob",
    }),
};

export function useGetInspectorPortalAssignments(
  query?: Partial<InspectorPortalAssignmentsQuery>,
): UseQueryResult<InspectorPortalAssignmentsResponse> {
  return useQuery({
    queryKey: [...INSPECTOR_PORTAL_QUERY_KEYS.ASSIGNMENTS(), query] as const,
    queryFn: () => apiCalls.getInspectorPortalAssignments(query),
  });
}

export function useGetInspectorPortalAssignmentDetail(assignmentId?: string) {
  return useQuery({
    queryKey: INSPECTOR_PORTAL_QUERY_KEYS.ASSIGNMENT_DETAIL(assignmentId),
    queryFn: () => apiCalls.getInspectorPortalAssignmentDetail(assignmentId!),
    enabled: Boolean(assignmentId),
  });
}
